import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from 'react';
import useErrors from '../../../../../hooks/useErrors';
import useApiCall from '../../../../../hooks/useApiCall';
import { toast } from 'react-toastify';
import { DateTimeOption, ProgramAttachmentItem } from '../../types';
import { useParams } from 'react-router-dom';
import activitiesService from '../../../../../services/activitiesService';
import { resolveAttachmentsToFiles } from '../../utils/attachments';

interface IUseAddModal {
  loadProgram: () => Promise<void>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setAddModalShow: Dispatch<SetStateAction<boolean>>;
}

export default function useAddModal({
  loadProgram,
  setIsLoading,
  setAddModalShow
}: IUseAddModal) {
  const [specificProgram, setSpecificProgram] = useState('');
  const [dateTime, setDateTime] = useState<DateTimeOption>({ date: null, startTime: null, endTime: null, id: null });
  const [attachments, setAttachments] = useState<ProgramAttachmentItem[]>([]);

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const { id } = useParams();

  const { apiCall } = useApiCall();

  function handleSpecificProgramChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setSpecificProgram(event.target.value);
    if (!event.target.value) {
      setError({ field: 'specificProgram', message: 'Nome da categoria é obrigatório!' });
    } else {
      removeError('specificProgram');
    }
  }

  const resetForm = useCallback(() => {
    setSpecificProgram('');
    setDateTime({ date: null, startTime: null, endTime: null, id: null });
    setAttachments([]);
  }, []);

  const addProgram = useCallback(async () => {
    setIsLoading(true);

    try {
      const files = await resolveAttachmentsToFiles(attachments);

      await apiCall({
        apiToCall: activitiesService.createActivityProgram,
        onStartLoad: () => { setIsLoading(true); },
        reqBody: [
          { key: 'activityId', value: id },
          { key: 'specificOrientation', value: specificProgram },
          { key: 'date', value: dateTime.date },
          { key: 'startTime', value: dateTime.startTime },
          { key: 'endTime', value: dateTime.endTime },
          ...files.map((file) => ({ key: 'attachments', value: file })),
        ],
        actionAfterResponse: (response) => {
          if (response.success) {
            toast.success('Programação enviada para aprovação com sucesso!');
            setAddModalShow(false);
            resetForm();
            loadProgram();
            return;
          }
          toast.error('Não foi possível adicionar a programação. Por favor, tente novamente');
          setIsLoading(false);
        },
        catchMessage: 'Não foi possível adicionar a programação. O upload dos anexos pode ter falhado. Por favor, tente novamente',
        catchAction: () => setIsLoading(false),
      });
    } catch (error) {
      console.log(error);
      toast.error('Não foi possível preparar os anexos para envio. Por favor, tente novamente');
      setIsLoading(false);
    }
  }, [apiCall, attachments, dateTime.date, dateTime.endTime, dateTime.startTime, id, loadProgram, resetForm, setAddModalShow, setIsLoading, specificProgram]);

  const isFormValid = !!specificProgram && !!dateTime.id && errors.length === 0;

  return {
    specificProgram,
    handleSpecificProgramChange,
    dateTime,
    setDateTime,
    getErrorMessageByFieldName,
    addProgram,
    isFormValid,
    attachments,
    setAttachments,
  };
}
